import React from 'react';
import { Settings, Trash2, Zap, Gauge, Radio, Building2 } from 'lucide-react';
import { GridElement, Connection } from '../../types';
import { GridEditorActions } from '../../GridEditorPresenter';
import { ElementManager } from './ElementManager';
import { LightManager } from './LightManager';
import { ConnectionManager } from './ConnectionManager';
import { Button } from '../../ui/Button';
import { MeterRealtimeMetrics } from '../../hooks/useMeterRealtimeMetrics';
import { RealTimeMetricsPanel } from './RealTimeMetricsPanel';

interface RightPropertiesPanelProps {
  selectedElement: GridElement | null;
  selectedConnection: Connection | null;
  meterMetrics: MeterRealtimeMetrics;
  lightTypes: Array<{ id: string; label: string; power: number }>;
  actions: Pick<GridEditorActions, 'updateElement' | 'addLight' | 'removeLight' | 'updateLight' | 'deleteElement' | 'deleteConnection' | 'updateConnection'>;
  isEditMode?: boolean;
}

const getTypeIcon = (type: string) => {
  switch (type) {
    case 'pole': return <Zap className="h-4 w-4 text-purple-500" />;
    case 'meter': return <Gauge className="h-4 w-4 text-blue-500" />;
    case 'transformer': return <Radio className="h-4 w-4 text-amber-500" />;
    case 'power-plant': return <Building2 className="h-4 w-4 text-green-500" />;
    default: return <Settings className="h-4 w-4 text-slate-500" />;
  }
};

const getTypeLabel = (type: string) => {
  switch (type) {
    case 'pole': return 'Propriétés du poteau';
    case 'meter': return 'Propriétés du compteur';
    case 'transformer': return 'Propriétés du transformateur';
    case 'power-plant': return 'Propriétés de la sous-station';
    default: return 'Propriétés de l\'élément';
  }
};

export const RightPropertiesPanel: React.FC<RightPropertiesPanelProps> = ({
  selectedElement,
  selectedConnection,
  meterMetrics,
  lightTypes,
  actions,
  isEditMode = false
}) => {
  // Always show the panel (even when nothing selected)
  return (
    <div className="w-72 h-full bg-white border-l border-slate-200 flex flex-col flex-shrink-0 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <Settings className="h-5 w-5 text-slate-500" />
          <h2 className="text-base font-semibold text-slate-800">Propriétés</h2>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Connection selected */}
        {selectedConnection && (
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-slate-700">
                Propriétés de la connexion
              </h3>
              {isEditMode && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => actions.deleteConnection(selectedConnection.id)}
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              )}
            </div>
            <ConnectionManager
              connection={selectedConnection}
              actions={actions}
            />
          </div>
        )}

        {/* Element selected */}
        {selectedElement && !selectedConnection && (
          <div className="p-4 space-y-3">
            {/* Element type header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {getTypeIcon(selectedElement.type)}
                <h3 className="text-sm font-semibold text-slate-700">
                  {getTypeLabel(selectedElement.type)} - {selectedElement.number || selectedElement.name || selectedElement.id.slice(-8)}
                </h3>
              </div>
              {isEditMode && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => actions.deleteElement(selectedElement.id)}
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              )}
            </div>

            {selectedElement.type === 'meter' && (
              <RealTimeMetricsPanel metrics={meterMetrics} />
            )}

            {/* Element Management (existing component) */}
            <ElementManager
              element={selectedElement}
              actions={actions}
              isEditMode={isEditMode}
            />

            {/* Lights Management for Poles */}
            {selectedElement.type === 'pole' && (
              <LightManager
                elementId={selectedElement.id}
                hostId={selectedElement.hostId}
                lights={selectedElement.lights || []}
                lightTypes={lightTypes}
                actions={actions}
                isEditMode={isEditMode}
              />
            )}
          </div>
        )}

        {/* Nothing selected */}
        {!selectedElement && !selectedConnection && (
          <div className="flex flex-col items-center justify-center h-64 text-center px-6">
            <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mb-3">
              <Settings className="h-6 w-6 text-slate-400" />
            </div>
            <p className="text-sm font-medium text-slate-500">Aucun élément sélectionné</p>
            <p className="text-xs text-slate-400 mt-1">
              Cliquez sur un élément de la carte pour voir ses propriétés
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
